import { Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useMsal } from '@azure/msal-react';
import { AmplitudeEventType } from '../../../../analytics/amplitudeEvents';
import { ClientConfig, type BrandProps } from '../../../../clientConfig';
import { ActivationCodeAuthorizationEvidence } from '../../../../types/authorizationEvidence';
import { ClientConfigContext } from '../../../clientConfigContext';
import { useBrand } from '../../../hooks/useBrand';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { useValidatedUrl } from '../../../hooks/useValidatedUrl';
import { sendEventByType } from '../../../services/amplitudeClient';
import { usePIMS } from '../../../services/pims/usePIMS';
import { getMyChartSamlSSOFromSystemAndBrand } from '../../../util/clientSideMyChartUtil';
import getAccessToken from '../../../util/getAccessToken';
import { redirectWithSoftLogout } from '../../../util/sessionTimeout';
import BrandedCard from '../../containers/BrandedCard/BrandedCard';
import CustomerServiceCard from '../../containers/CustomerServiceCard';
import TwoButtonContainer from '../../containers/TwoButtonContainer';
import withLogger, { ComponentLoggerProps } from '../../HOC/WithLogger';
import ActivationCodeForm, { ActivationCodeFormProps } from './ActivationCodeForm';

type ActivationCodeQueryParams = {
    client_id: string;
    cross_region: string;
    redirect_uri: string;
    redirect_url?: string;
    api_dep_condition?: string;
};

function ActivationCodePage({ log }: BrandProps & ComponentLoggerProps): JSX.Element {
    const history = useHistory();
    const brandInfo = useBrand();
    const { instance, accounts } = useMsal();
    const { handleSubmit, errors, control, formState } = useForm({ mode: 'onChange' });
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl();
    const { authorizeByActivationCode } = usePIMS();
    const { queryParams, error } = useQueryParams<ActivationCodeQueryParams>();

    const [jwt, setJwt] = useState('');
    const { isDirty, isValid } = formState;
    const { t } = useTranslation();

    useEffect(() => {
        getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log).then((jwt) => {
            sendEventByType(AmplitudeEventType.EHRActivationCode_CodeEntry, jwt);
            setJwt(jwt);
        });
    }, []);

    /**
     * SPI-1801: Used to test the API Deprecation
     * TO-DO: Remove before production
     */
    const [apiDepCondition, setApiDepCondition] = useState<string | undefined>(undefined);
    useEffect(() => {
        setApiDepCondition(queryParams?.api_dep_condition);
    }, [queryParams?.api_dep_condition]);

    // An error occurred while fetching query parameters
    if (error !== undefined) {
        log.error(`ActivationCodePage(): Error occurred - ${error}`);
        return <CustomerServiceCard />;
    }

    const { client_id: clientId, redirect_uri, redirect_url, cross_region } = queryParams;

    const isCrossRegion = cross_region === 'true';
    const redirectUrl = redirect_uri || redirect_url;

    const onSubmit = async (data: any) => {
        sendEventByType(AmplitudeEventType.EHRActivationCode_CodeEntry_Next, jwt);
        history.push('/creating');
        const authorizationEvidence: ActivationCodeAuthorizationEvidence = {
            activationCode: data.activationCode,
            last4SSN: data.ssn,
            dateOfBirth: data.birthdate,
        };
        try {
            const response = await authorizeByActivationCode({
                authorizationEvidence,
                brand: brandInfo.brand,
                clientId,
                apiDepCondition: apiDepCondition,
            });
            if (response.ok) {
                const body = await response.json();
                if (!body.authorizedSystem) {
                    log.error(`No AuthSystem, redirectUrl: ${redirectUrl}`);
                    sendEventByType(AmplitudeEventType.EHRActivationCode_CodeEntry_Invalid, jwt);
                    history.push('/error');
                    return;
                }
                sendEventByType(AmplitudeEventType.EHRAuthorized, jwt);
                // Cross Region is for MyChart-SAML flow. No other clients use set cross_region to true
                if (isCrossRegion) {
                    const mychartSamlSSOUrl = getMyChartSamlSSOFromSystemAndBrand(clientConfiguration, body.authorizedSystem, brandInfo.brand);
                    if (mychartSamlSSOUrl) {
                        redirectWithSoftLogout(instance, mychartSamlSSOUrl, validateRedirectUrl);
                    } else {
                        log.error(`Invalid MyChart SAML SSO URL: authorizedSystem: ${body.authorizedSystem}, brand: ${brandInfo.brand}`);
                        history.push('/error');
                    }
                } else {
                    log.debug(`Go to redirectURL: ${redirectUrl}`);
                    redirectWithSoftLogout(instance, redirectUrl, validateRedirectUrl);
                }
            } else {
                log.error(`ActivationCodePage(): Error occurred - ${response.status}${response.statusText ? ` -  ${response.statusText}` : ''}`);
                sendEventByType(AmplitudeEventType.EHRActivationCode_CodeEntry_Invalid, jwt);
                history.push('/phone-expired');
            }
        } catch (error) {
            log.error(`ActivationCodePage(): Error occurred - ${error}`);
            sendEventByType(AmplitudeEventType.EHRActivationCode_CodeEntry_Invalid, jwt);
            history.push('/phone-expired');
        }
    };

    return (
        <>
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ActivationCodeCard brandInfo={brandInfo} control={control} errors={errors} translation={t} isDirty={isDirty} isValid={isValid} />
                </form>
            </div>
        </>
    );
}

type ActivationCodeCardProps = BrandProps &
    ActivationCodeFormProps & {
        isDirty: boolean;
        isValid: boolean;
        translation: (key: string) => string;
    };

export function ActivationCodeCard({ brandInfo, control, errors, translation, isDirty, isValid }: ActivationCodeCardProps): JSX.Element {
    return (
        <BrandedCard brand={brandInfo}>
            {{
                header: <Typography variant='h2'>{translation('Enter your activation code')}</Typography>,
                content: <ActivationCodeForm control={control} errors={errors} />,
                actions: (
                    <TwoButtonContainer
                        firstButtonProps={{
                            variant: 'text',
                            label: translation('Skip'),
                            link: '/phone',
                            brand: brandInfo.brand,
                        }}
                        secondButtonProps={{
                            variant: 'contained',
                            disabled: !isDirty || !isValid,
                            label: translation('Next'),
                            brand: brandInfo.brand,
                        }}
                    />
                ),
            }}
        </BrandedCard>
    );
}

export default withLogger(ActivationCodePage, { eventSource: 'ActivationCodePage' });
